import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Versioned so the guide can be shown again to everyone once, by bumping this, if its
// content ever changes meaningfully enough to be worth re-surfacing.
const SEEN_KEY = "entnt.interviewGuideSeen.v1";

const hasSeenGuide = (): boolean => {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false; // localStorage unavailable (privacy mode, etc.) - fail open
  }
};

const markGuideSeen = () => {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Nothing to do if storage isn't available - worst case the guide reappears.
  }
};

/**
 * Decides whether the interview guide should open automatically.
 *
 * The signal is the `?guide=1` param on the candidate profile link sent in the
 * interviewer-notification emails (see CandidatesController/ActionService/
 * RolesController) - arriving via that link means this is, for certain, an
 * interviewer about to conduct or score an interview, not a recruiter browsing. It's
 * still gated on the localStorage flag so an interviewer who already knows the ropes
 * doesn't get the popup again on every new invite they click.
 *
 * The button next to Manual Evaluations opens the same modal on demand regardless of
 * this logic, and calling markSeen() from there (once they've asked for it once) means
 * it won't auto-open for them either, going forward.
 */
export default function useInterviewGuide() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("guide") !== "1") return;

    // Strip the param immediately either way, so refreshing or re-sharing the URL
    // doesn't keep re-triggering this, and the address bar stays clean.
    const next = new URLSearchParams(searchParams);
    next.delete("guide");
    setSearchParams(next, { replace: true });

    if (hasSeenGuide()) return;

    markGuideSeen();
    setOpen(true);
    // setSearchParams intentionally omitted - it's stable enough here in practice and
    // including it would re-run this on every param change we ourselves just made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openManually = () => {
    markGuideSeen();
    setOpen(true);
  };

  return { open, close: () => setOpen(false), openManually };
}
